<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'; // nextTick used by focus()
import { $t } from '@teachino/shared-i18n';
import type { ChatMessageInputProps } from './chat-message-input';
import { chatMessageInputDefaults } from './chat-message-input';
import IconButton from '../IconButton/IconButton.vue';
import TSpeechToText from '../SpeechToText/TSpeechToText.vue';
import TImageToText from '../ImageToText/TImageToText.vue';

const props = withDefaults(
  defineProps<ChatMessageInputProps>(),
  chatMessageInputDefaults,
);

const emit = defineEmits<{
  send: [message: string];
}>();

const text = ref('');
const textareaRef = ref<HTMLTextAreaElement | null>(null);

const canSend = computed(() => text.value.trim().length > 0 && !props.disabled);

const handleSend = (): void => {
  if (!canSend.value) return;
  emit('send', text.value.trim());
  text.value = '';
};

const focus = async (): Promise<void> => {
  await nextTick();
  textareaRef.value?.focus();
};

const insertTranscribedText = (transcribedText: string): void => {
  const normalizedText = transcribedText.trim();
  if (normalizedText.length === 0) {
    return;
  }

  const separator =
    text.value.length > 0 && !text.value.endsWith(' ') ? ' ' : '';
  text.value = `${text.value}${separator}${normalizedText}`;
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'Enter' && !event.metaKey && !event.shiftKey) {
    event.preventDefault();
    handleSend();
  }
};

defineExpose({ focus });
</script>

<template>
  <div
    class="flex items-center gap-2 rounded-2xl border border-neutral-300 bg-neutral-50 p-2 shadow-sm"
  >
    <textarea
      ref="textareaRef"
      v-model="text"
      class="max-h-40 flex-1 resize-none bg-transparent px-2 py-1.5 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none"
      :placeholder="props.placeholder ?? $t('chatbot.enter_message')"
      :disabled="disabled"
      @keydown="handleKeydown"
    />
    <TImageToText
      v-if="props.extractOcr"
      :disabled="disabled"
      :extract-text="props.extractOcr"
      extraction-error-label=""
      @extracted="(value) => void insertTranscribedText(value)"
    />
    <TSpeechToText
      v-if="props.transcribeStt"
      :disabled="disabled"
      :transcribe="props.transcribeStt"
      @transcribed="(value) => void insertTranscribedText(value)"
    />
    <IconButton
      icon="sent"
      variant="primary"
      size="md"
      :disabled="!canSend"
      @click="handleSend"
    />
  </div>
</template>
